import { useCallback, useMemo, useRef } from 'react';
import { Button, Link } from '@chakra-ui/react';
import type { ButtonBlock as ButtonBlockType, ProseMirrorDoc } from '../../editor/types';
import { useEditorBlockContext } from '../EditorBlockContext';
import { InlineTextEditor } from '../../editor/components/InlineTextEditor';
import { useResponsiveValue } from '../PreviewDeviceContext';

type ButtonBlockProps = ButtonBlockType['props'] & { blockId?: string };

const STYLE_MAP = {
  primary: {
    bg: 'blaze.blaze',
    color: 'white',
    _hover: { bg: 'blaze.700', textDecoration: 'none' },
  },
  secondary: {
    bg: 'transparent',
    border: '2px solid',
    borderColor: 'blaze.blaze',
    color: 'blaze.blaze',
    _hover: { bg: 'whiteAlpha.100', textDecoration: 'none' },
  },
  ghost: {
    bg: 'transparent',
    color: 'blaze.blaze',
    _hover: { bg: 'whiteAlpha.100', textDecoration: 'none' },
  },
} as const;

function labelToProseMirror(text: string): ProseMirrorDoc {
  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }],
  };
}

function proseMirrorToLabel(doc: ProseMirrorDoc): string {
  return (
    doc.content
      ?.map((node) => node.content?.map((child) => child.text ?? '').join('') ?? '')
      .join('') ?? ''
  );
}

export function ButtonBlock({
  label,
  href,
  style = 'primary',
  size = 'md',
  full_width,
  mobile_full_width,
  blockId,
}: ButtonBlockProps): React.ReactElement {
  const ctx = useEditorBlockContext();
  const isInEditor = ctx?.isEditorCanvas === true && blockId !== undefined;
  const styleProps = STYLE_MAP[style];
  const effectiveFullWidth = useResponsiveValue(mobile_full_width ?? full_width, full_width);

  const handleClick = (): void => {
    // Don't navigate or track in editor
    if (isInEditor) return;
    try {
      (window as unknown as { gc?: { track?: (event: string, payload: Record<string, unknown>) => void } })
        .gc?.track?.('landing_cta_clicked', {
          label,
          href,
          block_style: style,
        });
    } catch {
      // Silently ignore if tracking unavailable
    }
  };

  const proseMirrorContent = useMemo(() => labelToProseMirror(label), [label]);

  const pendingDocRef = useRef<ProseMirrorDoc | null>(null);

  const handleChange = useCallback(
    (doc: ProseMirrorDoc) => {
      pendingDocRef.current = doc;
    },
    [],
  );

  const handleBlur = useCallback(() => {
    if (pendingDocRef.current && ctx?.onBlockPropsChange && blockId) {
      const newLabel = proseMirrorToLabel(pendingDocRef.current);
      ctx.onBlockPropsChange(ctx.sectionId, blockId, { label: newLabel });
      pendingDocRef.current = null;
    }

  }, [ctx, blockId]);

  if (isInEditor) {
    return (
      <Button
        gc-testing-id="blockButtonEditing"
        as="span"
        size={size}
        width={effectiveFullWidth ? '100%' : undefined}
        textDecoration="none"
        cursor="text"
        onClick={(e: React.MouseEvent) => { e.stopPropagation(); ctx?.onSelectBlock(ctx.sectionId, blockId!); }}
        {...styleProps}
      >
        <InlineTextEditor
          content={proseMirrorContent}
          onChange={handleChange}
          onBlur={handleBlur}
          singleLine
          placeholder="Button label..."
        />
      </Button>
    );
  }

  return (
    <Button
      gc-testing-id="blockButton"
      as={Link}
      href={href}
      size={size}
      width={effectiveFullWidth ? '100%' : undefined}
      onClick={handleClick}
      textDecoration="none"
      {...styleProps}
    >
      {label}
    </Button>
  );
}
